import type { DashboardChartItem } from '@/lib/types';

interface SimpleBarChartProps {
  title: string;
  items: DashboardChartItem[];
  emptyMessage?: string;
  valueSuffix?: string;
  showSecondaryAsPercent?: boolean;
}

export default function SimpleBarChart({
  title,
  items,
  emptyMessage = 'No data to display yet.',
  valueSuffix = '',
  showSecondaryAsPercent = false,
}: SimpleBarChartProps) {
  const maxValue = Math.max(...items.map((item) => item.value), 0);

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm h-full">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {items.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">{emptyMessage}</p>
      ) : (
        <ul className="mt-5 space-y-4">
          {items.map((item) => {
            const width = maxValue > 0 ? Math.max((item.value / maxValue) * 100, 4) : 0;
            return (
              <li key={item.label}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-medium text-slate-700" title={item.label}>
                    {item.label}
                  </span>
                  <span className="shrink-0 tabular-nums text-slate-500">
                    {item.value}
                    {valueSuffix}
                    {showSecondaryAsPercent && item.secondary !== undefined
                      ? ` · ${item.secondary}% avg`
                      : ''}
                  </span>
                </div>
                <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-teal-400 transition-all"
                    style={{ width: `${width}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
